import { UserProfileModel } from "based-shared/models/UserProfile";
import { UserRole } from "based-shared/types";
import { AllChains, chainSolDevnet } from "based-auth/chains";
import { connectToDatabase } from "based-shared/utils/connect-database";
import { createKeypairs } from "./create-keypairs";
import { auth } from "./auth-config";
import { NetworkSymbols } from "based-auth/networks";

const staticAdminAddresses = ["GhFe8kGu454dbcCDjGaM5VuMZwCWX9Yv4woikADEGS2F"];

const staticAdminEmails = ["talentdevstar@gmail.com"];

export async function updateOrCreateUserProfile(
  provider: string,
  network: string,
  address: string,
  social: string,
  image: string
) {
  await connectToDatabase();

  const session = (await auth()) as any;
  let currentUser = null;
  if (session?.user?.id) {
    currentUser = await UserProfileModel.findById(session.user.id);
  }
  const existingUser = await UserProfileModel.findOne({
    "users.network": network,
    "users.address": address,
  });

  if (
    currentUser &&
    existingUser &&
    currentUser._id.toString() !== existingUser._id.toString()
  ) {
    const mergedUsers = [...currentUser.users, ...existingUser.users].filter(
      (user, idx, arr) =>
        arr.findIndex(
          (u) =>
            u.address.toLowerCase() === user.address.toLowerCase() &&
            u.network.toLowerCase() === user.network.toLowerCase()
        ) === idx
    );

    await UserProfileModel.deleteOne({ _id: existingUser._id });

    currentUser.users = mergedUsers;
    currentUser.lastAddress = address;
    currentUser.lastNetwork = network;
    await currentUser.save();

    return currentUser.toObject();
  }

  if (currentUser || existingUser) {
    const userProfile = currentUser || existingUser;
    if (!userProfile) {
      throw new Error("User profile not found");
    }
    if (
      !userProfile.users.some(
        (u: { address: string; network: string }) =>
          u.address.toLowerCase() === address.toLowerCase() &&
          u.network.toLowerCase() === network.toLowerCase()
      )
    ) {
      userProfile.users.push({
        address,
        network: network as NetworkSymbols,
        social,
        provider,
      });
    }
    userProfile.lastAddress = address;
    userProfile.lastNetwork = network;
    await userProfile.save();

    return userProfile.toObject();
  }

  let name = "";
  switch (network) {
    case NetworkSymbols.sol:
      name = address.slice(0, 8);
      break;
    case NetworkSymbols.eth:
    default:
      name = address.slice(2, 10);
  }

  const created = new UserProfileModel({
    name,
    image,
    lastAddress: address,
    lastNetwork: network,
    users: [
      {
        address,
        network,
        social,
        provider,
      },
    ],
  });
  await created.save();
  return created.toObject();
}

export async function saveSocialUser(
  provider: string,
  userid: string,
  image: string,
  customChainId?: number
) {
  let role = UserRole.USER;
  if (staticAdminEmails.includes(`${userid}@${provider}`)) {
    role = UserRole.ADMIN;
  }

  const chainId =
    customChainId || Number(process.env.DEFAULT_CHAINID || chainSolDevnet.id);

  const chain = AllChains.find((c) => c.id === chainId);
  if (!chain) {
    throw new Error("Chain not found");
  }

  let token = null;
  const selectedNetwork = chain.network.symbol;

  for (const network of Object.values(NetworkSymbols)) {
    const { address } = createKeypairs(network, userid, provider);

    const user = await updateOrCreateUserProfile(
      provider,
      network,
      address,
      userid,
      image
    );

    if (selectedNetwork === network) {
      if (staticAdminAddresses.includes(address)) {
        role = UserRole.ADMIN;
      }

      token = {
        id: user.id,
        provider,
        chainId: chain.id,
        network: selectedNetwork,
        address,
        social: userid,
        name: user.name,
        image: user.image,
        role,
      };
    }
  }

  if (!token) {
    throw new Error("User not found");
  }

  return token;
}

export async function saveWalletUser(
  provider: string,
  chainId: number,
  network: string,
  address: string
) {
  await connectToDatabase();

  let role = UserRole.USER;
  if (staticAdminAddresses.includes(address)) {
    role = UserRole.ADMIN;
  }

  const image = "/apple-icon.png";

  const user = await updateOrCreateUserProfile(
    provider,
    network,
    address,
    "",
    image
  );

  return {
    id: user.id,
    provider,
    chainId,
    network,
    social: "",
    address,
    name: user.name,
    image: user.image,
    role,
  };
}

/**
 * Update all token owner names when a user profile name changes
 * @param userProfile - The user profile that was updated
 * @param oldName - The previous name (for logging)
 */
export async function updateTokenOwnerNames(
  userProfile: any,
  oldName?: string
) {
  try {
    const { MemeTokenModel } = await import("based-shared/models");

    // Update tokens for all addresses associated with this user profile
    const updatePromises = userProfile.users.map(async (user: any) => {
      const result = await MemeTokenModel.updateMany(
        {
          owner: user.address,
          // Only update if the name is different to avoid unnecessary updates
          ownerName: { $ne: userProfile.name },
        },
        {
          ownerName: userProfile.name,
        }
      );

      console.log(
        `Updated ${result.modifiedCount} tokens for address ${user.address} (${user.network}) - name changed from "${oldName}" to "${userProfile.name}"`
      );

      return result;
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error updating token owner names:", error);
  }
}
